import type { GuideSelection } from "@/document/model/guides";
import {
  findSceneNode,
  getAllSceneNodes,
  getSceneNodeChildren,
  type CurvaDocument,
  type PrototypeLink,
} from "@/document/model/document";
import type { SceneNode } from "@/document/model/nodes";
import type { PrimitiveKind } from "@/document/model/primitives";
import { oklchToRgba } from "@/document/model/color";
import type { GradientPaint, ImagePaint } from "@/document/model/styles";
import { normalizeGradientPaint, normalizeImagePaint, normalizeStrokeStack } from "@/document/model/styles";
import type { RichTextModel } from "@/document/model/text";
import { nodeSelectionPointToWorld } from "@/document/operations/nodeWorldTransform";
import type { EditorMode } from "@/editor/store/editorStoreTypes";
import { selectionStore } from "@/editor/store/selectionStore";
import { uiStore } from "@/editor/store/uiStore";
import { viewStore } from "@/editor/store/viewStore";
import { buildDocumentGuideOverlay } from "@/editor/shell/guideInteraction";
import { rotationOriginOverlay } from "@/editor/shell/canvasGeometry";
import type { DragState } from "@/editor/shell/canvasWorkspaceTypes";
import type { ActiveTool } from "@/editor/shell/TopBar";
import type { PaintTarget } from "@/editor/shell/right-panel/types";
import { resolvePaintTarget } from "@/editor/shell/right-panel/paintStyles";
import {
  getNodesInLasso,
  getNodesInMarquee,
} from "@/editor/tools/hitTest";
import {
  resolveVectorAreaSelection,
} from "@/editor/tools/vectorEditHitTest";
import { computeMeasureOverlay, computeVectorMeasureOverlay } from "@/editor/tools/measureOverlay";
import type { ObjectSnapGuide } from "@/editor/tools/objectSnap";
import {
  gradientGizmoGeometry,
} from "@/editor/tools/gradientGizmo";
import {
  imageFillGeometry,
  type ImageFillHandle,
} from "@/editor/tools/imageFillGizmo";
import {
  primitiveGizmoLabel,
  primitiveGizmoPoints,
  type PrimitiveGizmoHandle,
} from "@/editor/tools/primitiveGizmo";
import type { Rect } from "@/lib/math/rect";
import { boundsFromPoints, rectCorners } from "@/lib/math/rotation";
import type { Camera } from "@/renderer/camera";
import type {
  FrameLabelOverlay,
  GradientGizmoOverlay,
  GuidePreviewOverlay,
  ImageFillGizmoOverlay,
  KnifePreviewOverlay,
  PrimitiveGizmoOverlay,
  PrototypeAnchorOverlay,
  PrototypeLinkOverlay,
  RenderOverlay,
  RenderPreview,
  SceneOverlay,
  SelectionOverlay,
  ShapeBuilderRegionOverlay,
  TextSelectionOverlay,
  VectorEditOverlay,
  VectorSmartTransformOverlay,
} from "@/renderer/overlayTypes";
import type { ViewportScreenInsets } from "@/renderer/renderHelpers";
import type { VectorVertexPreviewState } from "@/editor/store/editorStoreTypes";
import type { PointerHoverState } from "@/editor/shell/pointerHover";

export type RenderOverlayOverrides = Partial<
  Pick<SelectionOverlay, "selectedNodeIds" | "objectSnapGuides">
  & Pick<SceneOverlay, "previews" | "draftShape">
  & Pick<VectorEditOverlay, "edgeBendPreview">
>;

export interface BuildRenderOverlayInput {
  document: CurvaDocument;
  camera: Camera;
  overrides?: RenderOverlayOverrides;
  activePageId?: string;
  mode: EditorMode;
  activeTool: ActiveTool;
  selectedNodeIds: string[];
  editableSelectedNodeIds: string[];
  primarySelectedNodeId?: string;
  selectedVertexId?: string;
  selectedVertexIds: string[];
  selectedEdgeIds: string[];
  selectedHandle?: { edgeId: string; endpoint: "from" | "to" };
  devModeActive: boolean;
  prototypeModeActive: boolean;
  prototypeLinks: PrototypeLink[];
  selectedPrototypeLinkId?: string;
  hover: PointerHoverState;
  drag: DragState | null;
  measureModifierActive: boolean;
  objectSnapGuides: ObjectSnapGuide[];
  activeGuideSelection?: GuideSelection;
  guidePreview?: GuidePreviewOverlay;
  selectionBounds?: Rect;
  draftShape?: { kind: PrimitiveKind; bounds: Rect };
  draftVectorNetwork?: NonNullable<SceneOverlay["draftVectorNetwork"]>;
  draftComponentInstance?: NonNullable<SceneOverlay["draftComponentInstance"]>;
  knifePreview?: KnifePreviewOverlay;
  originPreview?: { nodeId: string; x: number; y: number; snapPoints: Array<{ x: number; y: number }> };
  prototypeLinkPreview?: SelectionOverlay["prototypeLinkPreview"];
  animationFrameOverlay?: { frameId: string; bounds: Rect };
  visibleRotationOriginNodeId?: string;
  visibleViewportInsets?: ViewportScreenInsets;
  shapeBuilderOverlayRegions: ShapeBuilderRegionOverlay[];
  frameLabelItems: FrameLabelOverlay[];
  vectorSmartTransformBox?: VectorSmartTransformOverlay;
  renderTextSelection?: TextSelectionOverlay;
  textPreviews?: Map<string, RichTextModel>;
  activeGradientHandle?: string;
  activeImageHandle?: ImageFillHandle;
  vectorTargetNodeId?: string;
}

interface ActiveGradientPaint {
  node: SceneNode;
  target: PaintTarget;
  paint: GradientPaint;
}

interface ActiveImageFillPaint {
  node: SceneNode;
  target: PaintTarget;
  paint: ImagePaint;
}

export function buildRenderOverlay(input: BuildRenderOverlayInput): RenderOverlay {
  const {
    document,
    camera: activeCamera,
    overrides,
    activePageId,
    mode,
    activeTool,
    selectedNodeIds,
    editableSelectedNodeIds,
    primarySelectedNodeId,
    selectedVertexId,
    selectedVertexIds,
    selectedEdgeIds,
    selectedHandle,
    devModeActive,
    prototypeModeActive,
    prototypeLinks,
    selectedPrototypeLinkId,
    hover,
    drag,
    measureModifierActive,
    objectSnapGuides,
    activeGuideSelection,
    guidePreview,
    selectionBounds,
    draftShape: inputDraftShape,
    draftVectorNetwork,
    draftComponentInstance,
    knifePreview,
    originPreview,
    prototypeLinkPreview,
    animationFrameOverlay,
    visibleRotationOriginNodeId,
    visibleViewportInsets,
    shapeBuilderOverlayRegions,
    frameLabelItems,
    vectorSmartTransformBox,
    renderTextSelection,
    textPreviews,
    activeGradientHandle,
    activeImageHandle,
    vectorTargetNodeId,
  } = input;

  const previews = overrides?.previews ?? viewStore.previews.getValue();
  const draftShape = overrides && "draftShape" in overrides ? overrides.draftShape : inputDraftShape;
  const vertexPreview = viewStore.vertexPreview.getValue();
  const handlePreview = viewStore.handlePreview.getValue();
  const edgeBendPreview = overrides?.edgeBendPreview ?? viewStore.edgeBendPreview.getValue();
  const sculptBrushPreview = viewStore.sculptBrushPreview.getValue();
  const vectorTopologyPreview = viewStore.vectorTopologyPreview.getValue();
  const activeColorSampler = uiStore.colorSampler.getValue();
  const canvasDebug = uiStore.canvasDebug.getValue();
  const activeFreshCutVertexIds = selectionStore.freshCutVertexIds.getValue();
  const editingGradientFillTarget = uiStore.editingGradientFillTarget.getValue();
  const editingImageFillTarget = uiStore.editingImageFillTarget.getValue();
  const hideEditorChrome = Boolean(activeColorSampler);

  const activeGradient = activeGradientForSelection(
    document,
    primarySelectedNodeId,
    editableSelectedNodeIds,
    editingGradientFillTarget,
  );
  const activeImageFill = activeImageFillForSelection(
    document,
    primarySelectedNodeId,
    editableSelectedNodeIds,
    editingImageFillTarget,
  );

  const gradientGizmo = activeGradient
    ? gradientGizmoOverlay(
      document,
      activeGradient,
      hover.hoveredGradientHandle ?? activeGradientHandle,
      activeCamera.zoom,
      previews,
    )
    : undefined;
  const imageFillGizmo = activeImageFill
    ? imageFillGizmoOverlay(document, activeImageFill, hover.hoveredImageHandle ?? activeImageHandle, previews)
    : undefined;
  const primitiveGizmo = buildPrimitiveGizmoOverlay(
    document,
    editableSelectedNodeIds,
    mode,
    hover.hoveredPrimitiveGizmoHandle,
    drag,
    activeCamera.zoom,
    previews,
  );
  const { marquee, lasso, previewSelectedNodeIds, previewSelectedVertexIds, previewSelectedEdgeIds } = resolveMarqueeOverlay({
    document,
    activePageId,
    mode,
    primarySelectedNodeId,
    vectorTargetNodeId,
    activePreviews: previews,
    drag,
  });

  return {
    activePageId,
    hiddenNodeIdsPreserveOverlayLookup: animationFrameOverlay ? [animationFrameOverlay.frameId] : undefined,
    selectedNodeIds: hideEditorChrome ? [] : overrides?.selectedNodeIds ?? editableSelectedNodeIds,
    hoveredNodeId: hideEditorChrome ? undefined : hover.hoveredNodeId,
    hoveredFrameLabelId: hideEditorChrome ? undefined : hover.hoveredFrameLabelId,
    hoveredResizeHandle: hideEditorChrome || devModeActive ? undefined : hover.hoveredResizeHandle,
    hoveredRotationHandle: hideEditorChrome || devModeActive ? undefined : hover.hoveredRotationHandle,
    hoveredVertexId: hideEditorChrome ? undefined : hover.hoveredVertexId,
    hoveredVectorHandle: hideEditorChrome ? undefined : hover.hoveredVectorHandle,
    shapeBuilderRegions: hideEditorChrome ? [] : shapeBuilderOverlayRegions,
    previewSelectedNodeIds: hideEditorChrome ? [] : previewSelectedNodeIds,
    previewSelectedVertexIds: hideEditorChrome ? [] : previewSelectedVertexIds,
    previewSelectedEdgeIds: hideEditorChrome ? [] : previewSelectedEdgeIds,
    previews,
    frameLabels: hideEditorChrome ? [] : frameLabelItems,
    objectSnapGuides: hideEditorChrome ? [] : overrides?.objectSnapGuides ?? objectSnapGuides,
    documentGuides: hideEditorChrome ? [] : buildDocumentGuideOverlay(document, activePageId, editableSelectedNodeIds, activeGuideSelection),
    guidePreview: hideEditorChrome ? undefined : guidePreview,
    measureOverlay: !hideEditorChrome && (measureModifierActive || devModeActive) &&
      mode === "select" &&
      activeTool === "select" &&
      !drag &&
      !activeColorSampler &&
      editableSelectedNodeIds.length > 0 &&
      selectionBounds
      ? computeMeasureOverlay(document, editableSelectedNodeIds, selectionBounds, hover.hoveredNodeId)
      : undefined,
    devMode: devModeActive,
    vectorMeasureOverlay: !hideEditorChrome && measureModifierActive &&
      mode === "vectorEdit" &&
      primarySelectedNodeId &&
      !drag &&
      (selectedVertexIds.length > 0 || hover.hoveredVertexId !== undefined || hover.hoveredEdgeId !== undefined)
      ? computeVectorMeasureOverlay(document, primarySelectedNodeId, selectedVertexIds, hover.hoveredVertexId, hover.hoveredEdgeId) ?? undefined
      : undefined,
    visibleViewportInsets,
    selectionBounds: hideEditorChrome ? undefined : selectionBounds,
    marquee: hideEditorChrome ? undefined : marquee,
    lasso: hideEditorChrome ? undefined : lasso,
    draftShape,
    draftVectorNetwork,
    draftComponentInstance,
    vectorEditNodeId: hideEditorChrome ? undefined : (mode === "vectorEdit" ? primarySelectedNodeId : undefined) ?? vectorTargetNodeId,
    selectedVertexId: hideEditorChrome ? undefined : selectedVertexId,
    selectedVertexIds: hideEditorChrome ? [] : selectedVertexIds,
    selectedEdgeIds: hideEditorChrome ? [] : selectedEdgeIds,
    freshCutVertexIds: hideEditorChrome ? [] : activeFreshCutVertexIds,
    hoveredEdgeId: hideEditorChrome ? undefined : hover.hoveredEdgeId,
    knifePreview: hideEditorChrome ? undefined : knifePreview,
    vertexPreview: hideEditorChrome ? undefined : vertexPreview,
    vectorSmartTransform: hideEditorChrome ? undefined : vectorSmartTransformBox,
    selectedHandle: hideEditorChrome ? undefined : selectedHandle,
    handlePreview: hideEditorChrome ? undefined : handlePreview,
    edgeBendPreview: hideEditorChrome ? undefined : edgeBendPreview,
    sculptBrushPreview: hideEditorChrome ? undefined : sculptBrushPreview,
    vectorTopologyPreview: hideEditorChrome ? undefined : vectorTopologyPreview,
    pathPreviewPoint: hideEditorChrome ? undefined : hover.pathPreviewPoint,
    pathPreviewSegment: hideEditorChrome ? undefined : hover.pathPreviewSegment,
    gradientGizmo: hideEditorChrome ? undefined : gradientGizmo,
    imageFillGizmo: hideEditorChrome ? undefined : imageFillGizmo,
    primitiveGizmo: hideEditorChrome ? undefined : primitiveGizmo,
    rotationOriginGizmo: hideEditorChrome ? undefined : rotationOriginOverlay(
      document,
      visibleRotationOriginNodeId,
      editableSelectedNodeIds,
      hover.hoveredRotationOrigin,
      drag?.mode === "origin",
      originPreview,
      previews,
    ),
    prototypeLinks: !hideEditorChrome && prototypeModeActive
      ? prototypeLinkOverlays(
        document,
        prototypeLinksForOverlay(document, prototypeLinks, selectedNodeIds, selectedPrototypeLinkId),
        selectedPrototypeLinkId,
      )
      : undefined,
    prototypeAnchors: !hideEditorChrome && prototypeModeActive
      ? prototypeAnchorOverlays(document, [
        ...selectedNodeIds,
        ...(hover.hoveredPrototypeNodeId ? [hover.hoveredPrototypeNodeId] : []),
        ...(drag?.mode === "prototypeLink" && drag.prototypeSourceNodeId ? [drag.prototypeSourceNodeId] : []),
      ], hover.hoveredPrototypeNodeId)
      : undefined,
    prototypeLinkPreview: hideEditorChrome ? undefined : prototypeLinkPreview,
    textSelection: hideEditorChrome ? undefined : renderTextSelection,
    textPreviews,
    showPixelGrid: hideEditorChrome ? false : canvasDebug.showPixelGrid,
    showRenderDebug: hideEditorChrome ? false : canvasDebug.showRenderDebug,
    ignoreChildConstraints: drag?.mode === "resize" ? drag.ignoreChildConstraints : undefined,
  };
}

export function vectorSmartTransformOverlay(
  document: CurvaDocument,
  mode: EditorMode,
  nodeId: string | undefined,
  selectedVertexIds: string[],
  vertexPreview: VectorVertexPreviewState | undefined,
  rotation: number,
): (VectorSmartTransformOverlay & { vertices: Array<{ vertexId: string; x: number; y: number }> }) | undefined {
  if (mode !== "vectorEdit" || !nodeId || selectedVertexIds.length <= 1) {
    return undefined;
  }

  const node = getAllSceneNodes(document).find((candidate): candidate is Extract<SceneNode, { type: "vectorNetwork" }> => candidate.id === nodeId && candidate.type === "vectorNetwork");
  if (!node) {
    return undefined;
  }

  const previewById = new Map(
    vertexPreview?.nodeId === node.id
      ? (vertexPreview.vertices ?? [vertexPreview]).map(vertex => [vertex.vertexId, vertex] as const)
      : [],
  );
  const selected = node.network.vertices.flatMap(vertex => {
    if (!selectedVertexIds.includes(vertex.id)) {
      return [];
    }

    const preview = previewById.get(vertex.id);
    return [{ vertexId: vertex.id, x: preview?.x ?? vertex.x, y: preview?.y ?? vertex.y }];
  });
  if (selected.length <= 1) {
    return undefined;
  }

  return {
    nodeId,
    bounds: boundsForVectorVertexSelection(node, selected, rotation),
    rotation,
    vertices: selected,
  };
}

function resolveMarqueeOverlay({
  document,
  activePageId,
  mode,
  primarySelectedNodeId,
  vectorTargetNodeId,
  activePreviews,
  drag,
}: {
  document: CurvaDocument;
  activePageId?: string;
  mode: EditorMode;
  primarySelectedNodeId?: string;
  vectorTargetNodeId?: string;
  activePreviews: RenderPreview[];
  drag: DragState | null;
}): {
  marquee: Rect | undefined;
  lasso: SelectionOverlay["lasso"] | undefined;
  previewSelectedNodeIds: string[];
  previewSelectedVertexIds: string[];
  previewSelectedEdgeIds: string[];
} {
  const marqueePreview = activePreviews.find(preview => preview.nodeId === "__marquee__");
  const sceneMarquee = drag?.mode === "marquee" || drag?.mode === "vectorMarquee";
  const vectorSelectionNodeId = vectorTargetNodeId ?? (mode === "vectorEdit" ? primarySelectedNodeId : undefined);
  const marquee =
    (sceneMarquee || drag?.mode === "animationMarquee") && drag?.moved && marqueePreview?.bounds
      ? marqueePreview.bounds
      : undefined;
  const lasso = drag?.mode === "lasso" && drag.moved && drag.lassoPoints && drag.lassoPoints.length >= 2
    ? { points: drag.lassoPoints }
    : undefined;

  const vectorAreaHit = vectorSelectionNodeId !== undefined && drag?.mode !== "animationMarquee"
    ? marquee
      ? resolveVectorAreaSelection(document, vectorSelectionNodeId, { type: "rect", rect: marquee })
      : lasso && lasso.points.length >= 3
        ? resolveVectorAreaSelection(document, vectorSelectionNodeId, { type: "lasso", points: lasso.points })
        : undefined
    : undefined;

  return {
    marquee,
    lasso,
    previewSelectedNodeIds: mode === "select" && !vectorTargetNodeId && drag?.mode !== "animationMarquee"
      ? marquee
        ? getNodesInMarquee(document, marquee, activePageId)
        : lasso && lasso.points.length >= 3
          ? getNodesInLasso(document, lasso.points, activePageId)
          : []
      : [],
    previewSelectedVertexIds: vectorAreaHit?.kind === "vertices" ? vectorAreaHit.ids : [],
    previewSelectedEdgeIds: vectorAreaHit?.kind === "edges" ? vectorAreaHit.ids : [],
  };
}

function prototypeLinksForOverlay(
  document: CurvaDocument,
  links: PrototypeLink[],
  selectedNodeIds: string[],
  selectedPrototypeLinkId?: string,
): PrototypeLink[] {
  if (selectedNodeIds.length === 0) {
    return links;
  }

  const selected = new Set(selectedNodeIds);
  const selectedFrameNodeIds = new Set<string>();
  for (const nodeId of selectedNodeIds) {
    const node = findSceneNode(document, nodeId);
    if (node?.type === "frame") {
      selectedFrameNodeIds.add(node.id);
      for (const descendantId of descendantNodeIds(node)) {
        selectedFrameNodeIds.add(descendantId);
      }
    }
  }

  return links.filter(link =>
    selected.has(link.sourceNodeId) ||
    selected.has(link.targetFrameId) ||
    selectedFrameNodeIds.has(link.sourceNodeId) ||
    selectedFrameNodeIds.has(link.targetFrameId) ||
    link.id === selectedPrototypeLinkId,
  );
}

function prototypeLinkOverlays(
  document: CurvaDocument,
  links: PrototypeLink[],
  selectedPrototypeLinkId?: string,
): PrototypeLinkOverlay[] {
  return links.flatMap(link => {
    const source = findSceneNode(document, link.sourceNodeId);
    const target = findSceneNode(document, link.targetFrameId);
    return source && target?.type === "frame"
      ? [{ ...link, from: nodeCenter(source), to: nodeCenter(target), selected: link.id === selectedPrototypeLinkId }]
      : [];
  });
}

function prototypeAnchorOverlays(
  document: CurvaDocument,
  nodeIds: string[],
  hoveredNodeId?: string,
): PrototypeAnchorOverlay[] {
  const seen = new Set<string>();
  return nodeIds.flatMap(nodeId => {
    if (seen.has(nodeId)) {
      return [];
    }
    seen.add(nodeId);
    const node = findSceneNode(document, nodeId);
    if (!node) {
      return [];
    }
    const center = nodeCenter(node);
    return [{ nodeId, x: center.x, y: center.y, active: true, hovered: hoveredNodeId === nodeId }];
  });
}

function nodeCenter(node: SceneNode): { x: number; y: number } {
  return {
    x: node.bounds.x + node.bounds.width / 2,
    y: node.bounds.y + node.bounds.height / 2,
  };
}

function descendantNodeIds(node: SceneNode): string[] {
  const children = getSceneNodeChildren(node);
  if (!children) {
    return [];
  }
  return children.flatMap(child => [child.id, ...descendantNodeIds(child)]);
}

export function activeGradientForSelection(
  document: CurvaDocument,
  primarySelectedNodeId: string | undefined,
  selectedNodeIds: string[],
  editingGradientFillTarget: { nodeId: string; target: PaintTarget } | undefined,
): ActiveGradientPaint | undefined {
  if (!primarySelectedNodeId || selectedNodeIds.length !== 1 || editingGradientFillTarget?.nodeId !== primarySelectedNodeId) {
    return undefined;
  }

  const node = getAllSceneNodes(document).find(candidate => candidate.id === primarySelectedNodeId);
  const target = editingGradientFillTarget ? resolvePaintTarget(editingGradientFillTarget.target) : undefined;
  const paint = node && target?.type === "fill"
    ? node.style.fills[target.index]
    : node && target?.type === "stroke"
      ? normalizeStrokeStack(node.style)[target.index]?.paint
      : undefined;
  if (!node || !target || (paint?.type !== "linearGradient" && paint?.type !== "radialGradient")) {
    return undefined;
  }

  return {
    node,
    target: editingGradientFillTarget!.target,
    paint: normalizeGradientPaint(paint),
  };
}

export function activeImageFillForSelection(
  document: CurvaDocument,
  primarySelectedNodeId: string | undefined,
  selectedNodeIds: string[],
  editingImageFillTarget: { nodeId: string; target: PaintTarget } | undefined,
): ActiveImageFillPaint | undefined {
  if (!primarySelectedNodeId || selectedNodeIds.length !== 1 || editingImageFillTarget?.nodeId !== primarySelectedNodeId) {
    return undefined;
  }

  const node = getAllSceneNodes(document).find(candidate => candidate.id === primarySelectedNodeId);
  const target = editingImageFillTarget ? resolvePaintTarget(editingImageFillTarget.target) : undefined;
  const fillIndex = target?.type === "fill" ? target.index : -1;
  const fill = node && fillIndex >= 0 ? node.style.fills[fillIndex] : undefined;
  if (!node || fill?.type !== "image") {
    return undefined;
  }

  return {
    node,
    target: { type: "fill", index: fillIndex },
    paint: normalizeImagePaint(fill),
  };
}

function gradientGizmoOverlay(
  document: CurvaDocument,
  activeGradient: ActiveGradientPaint,
  activeHandle: string | undefined,
  zoom: number,
  previews: RenderPreview[],
): GradientGizmoOverlay {
  const geometry = gradientGizmoGeometry(activeGradient.node.bounds, activeGradient.paint, zoom);
  const normalized = normalizeGradientPaint(activeGradient.paint);
  const toWorld = (point: { x: number; y: number }): { x: number; y: number } =>
    nodeSelectionPointToWorld(document, activeGradient.node.id, point, previews);
  const start = toWorld(geometry.type === "radial" ? geometry.center : geometry.start);
  const end = toWorld(geometry.type === "radial" ? geometry.radiusPoint : geometry.end);
  const rendererActiveHandle = geometry.type === "radial"
    ? activeHandle === "center"
      ? "start"
      : activeHandle === "radius"
        ? "end"
        : activeHandle
    : activeHandle;
  return {
    start,
    end,
    activeHandle: rendererActiveHandle,
    stops: geometry.stops.map(stop => {
      const color = normalized.stops.find(candidate => candidate.id === stop.id)?.color;
      const point = toWorld(stop);
      const anchor = toWorld({ x: stop.anchorX, y: stop.anchorY });
      return {
        id: stop.id,
        x: point.x,
        y: point.y,
        anchorX: anchor.x,
        anchorY: anchor.y,
        color: color ? oklchToRgba(color) : [0, 0, 0, 1],
        selected: activeHandle === stop.id,
      };
    }),
  };
}

function imageFillGizmoOverlay(
  document: CurvaDocument,
  activeImageFill: ActiveImageFillPaint,
  activeHandle: ImageFillHandle | undefined,
  previews: RenderPreview[],
): ImageFillGizmoOverlay {
  const geometry = imageFillGeometry(activeImageFill.node.bounds, activeImageFill.paint);
  const toWorld = (point: { x: number; y: number }): { x: number; y: number } =>
    nodeSelectionPointToWorld(document, activeImageFill.node.id, point, previews);
  const corners = rectCorners(geometry.imageRect).map(toWorld);
  return {
    imageRect: boundsFromPoints(corners),
    corners,
    activeHandle,
    handles: geometry.handles.map(handle => ({ ...handle, ...toWorld(handle) })),
  };
}

function buildPrimitiveGizmoOverlay(
  document: CurvaDocument,
  selectedNodeIds: string[],
  mode: EditorMode,
  hoveredHandle: PrimitiveGizmoHandle | undefined,
  drag: DragState | null,
  zoom: number,
  previews: RenderPreview[],
): PrimitiveGizmoOverlay | undefined {
  if (mode !== "select" || selectedNodeIds.length !== 1) {
    return undefined;
  }

  // The gizmo positions are derived from the node's original geometry, so they
  // don't follow the live move/resize/rotate preview and would visibly lag
  // behind the bbox until the drag ends. Hide them while such a transform drag
  // is active (the primitiveGizmo drag itself keeps showing its handles).
  if (drag?.mode === "move" || drag?.mode === "resize" || drag?.mode === "rotate") {
    return undefined;
  }

  const nodeId = selectedNodeIds[0]!;
  const node = getAllSceneNodes(document).find(candidate => candidate.id === nodeId);
  if (!node || node.type !== "primitive") {
    return undefined;
  }

  const toWorld = (point: { x: number; y: number }) => nodeSelectionPointToWorld(document, nodeId, point, previews);
  const gizmos = primitiveGizmoPoints(node, zoom);
  const activeHandle = drag?.mode === "primitiveGizmo" ? drag.primitiveGizmoHandle : undefined;

  const handles = gizmos.map(gizmo => {
    const world = toWorld({ x: gizmo.x, y: gizmo.y });
    const active = activeHandle ? primitiveGizmoHandleKey(activeHandle) === primitiveGizmoHandleKey(gizmo.handle) : false;
    const hovered = hoveredHandle ? primitiveGizmoHandleKey(hoveredHandle) === primitiveGizmoHandleKey(gizmo.handle) : false;
    return { id: primitiveGizmoHandleKey(gizmo.handle), x: world.x, y: world.y, active, hovered };
  });

  const labelHandle = activeHandle ?? hoveredHandle;
  let label: { x: number; y: number; text: string } | undefined;
  if (labelHandle) {
    const labelGizmo = gizmos.find(gizmo => primitiveGizmoHandleKey(gizmo.handle) === primitiveGizmoHandleKey(labelHandle));
    if (labelGizmo) {
      const world = toWorld({ x: labelGizmo.x, y: labelGizmo.y });
      const text = primitiveGizmoLabel(node.primitive, labelHandle);
      if (text) {
        label = { x: world.x, y: world.y, text };
      }
    }
  }

  return { handles, label };
}

function primitiveGizmoHandleKey(handle: PrimitiveGizmoHandle): string {
  return handle.type;
}

function boundsForVectorVertexSelection(
  node: Extract<SceneNode, { type: "vectorNetwork" }>,
  selected: Array<{ vertexId: string; x: number; y: number }>,
  rotation: number,
): Rect {
  const points = selected.flatMap(vertex => {
    const coords = [{ x: vertex.x, y: vertex.y }];
    for (const edge of node.network.edges) {
      if (edge.from.vertexId === vertex.vertexId && edge.from.handle) {
        coords.push(edge.from.handle);
      }
      if (edge.to.vertexId === vertex.vertexId && edge.to.handle) {
        coords.push(edge.to.handle);
      }
    }
    return coords;
  });
  return rotation === 0 ? boundsFromVertexPositions(points) : orientedBoundsFromVertexPositions(points, rotation);
}

function boundsFromVertexPositions(vertices: Array<{ x: number; y: number }>): Rect {
  const left = Math.min(...vertices.map(vertex => vertex.x));
  const top = Math.min(...vertices.map(vertex => vertex.y));
  const right = Math.max(...vertices.map(vertex => vertex.x));
  const bottom = Math.max(...vertices.map(vertex => vertex.y));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

function orientedBoundsFromVertexPositions(vertices: Array<{ x: number; y: number }>, rotation: number): Rect {
  const radians = (rotation * Math.PI) / 180;
  const axisX = { x: Math.cos(radians), y: Math.sin(radians) };
  const axisY = { x: -Math.sin(radians), y: Math.cos(radians) };
  const projected = vertices.map(vertex => ({
    x: vertex.x * axisX.x + vertex.y * axisX.y,
    y: vertex.x * axisY.x + vertex.y * axisY.y,
  }));
  const minX = Math.min(...projected.map(point => point.x));
  const maxX = Math.max(...projected.map(point => point.x));
  const minY = Math.min(...projected.map(point => point.y));
  const maxY = Math.max(...projected.map(point => point.y));
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;
  const center = {
    x: axisX.x * centerX + axisY.x * centerY,
    y: axisX.y * centerX + axisY.y * centerY,
  };
  const width = maxX - minX;
  const height = maxY - minY;
  return {
    x: center.x - width / 2,
    y: center.y - height / 2,
    width,
    height,
  };
}
